import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const ORDER = ["locations", "registers", "staff"];

function UsageBadge({ it }) {
  if (it.custom) return <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/20 text-[10px]">Custom limit</Badge>;
  if (it.over_standard) return <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-[10px]">Over Standard</Badge>;
  return null;
}

function Row({ kind, it, onEdit }) {
  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-testid={`ent-row-${kind}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-head font-bold text-sm">{it.label}</p>
        <UsageBadge it={it} />
      </div>
      <p className="text-2xl font-head font-extrabold" data-testid={`ent-usage-${kind}`}>{it.used} <span className="text-sm font-medium text-muted-foreground">of {it.effective}</span></p>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div><p className="text-muted-foreground">Standard</p><p className="font-bold" data-testid={`ent-standard-${kind}`}>{it.standard} included</p></div>
        <div><p className="text-muted-foreground">Override</p><p className="font-bold" data-testid={`ent-override-${kind}`}>{it.override ?? "None"}</p></div>
        <div><p className="text-muted-foreground">Effective</p><p className="font-bold" data-testid={`ent-effective-${kind}`}>{it.effective}</p></div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" onClick={() => onEdit(kind, "apply")} data-testid={`ent-apply-btn-${kind}`}>{it.custom ? "Change Override" : "Apply Override"}</Button>
        {it.custom && <Button size="sm" variant="ghost" onClick={() => onEdit(kind, "clear")} data-testid={`ent-clear-btn-${kind}`}>Clear Override</Button>}
      </div>
    </div>
  );
}

// Per-location register usage + the per-tenant enforcement flag (reason required, audited).
function RegistersPanel({ client }) {
  const [info, setInfo] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get(`/superadmin/clients/${client.id}/registers`).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { if (client) load(); }, [client?.id]); // eslint-disable-line
  if (!info) return null;
  const on = info.flags.register_enforcement_enabled;
  const sOn = info.flags.staff_location_enforcement_enabled;
  const toggle = async (flag) => {
    const cur = flag === "staff" ? sOn : on;
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    if (!window.confirm(`${cur ? "Disable" : "Enable"} ${flag === "staff" ? "staff-location" : "POS register"} enforcement for ${client.business_name}?${cur ? "" : "\n\nUnregistered devices / unassigned staff will be blocked from processing sales."}`)) return;
    try { await api.put(`/superadmin/clients/${client.id}/register-enforcement`, { enabled: !cur, reason: reason.trim(), flag }); setReason(""); load(); toast.success(`Enforcement ${cur ? "disabled" : "enabled"}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-xl border border-border p-3" data-testid="ent-registers-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Registers by location</p>
        <span className={`text-[11px] font-bold ${on ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="ent-enforcement-state">Registers {on ? "ON" : "OFF"} · Staff {sOn ? "ON" : "OFF"}</span>
      </div>
      {info.locations.length === 0 && <p className="text-xs text-muted-foreground" data-testid="ent-no-locations">No locations set up yet (implicit single location).</p>}
      {info.locations.map((l) => <div key={l.id} className="flex justify-between text-sm py-0.5" data-testid={`ent-loc-${l.id}`}><span>{l.name}</span><span><span className={`font-bold ${l.at_limit ? "text-amber-600" : ""}`}>Registers {l.registers_used}/{l.registers_limit}</span><span className={`font-bold ml-3 ${l.staff_at_limit ? "text-amber-600" : ""}`} data-testid={`ent-loc-staff-${l.id}`}>Staff {l.staff_used}/{l.staff_limit}</span></span></div>)}
      {info.legacy_unassigned > 0 && <p className="text-xs text-amber-600 mt-1" data-testid="ent-legacy-count">{info.legacy_unassigned} legacy register(s) awaiting location assignment</p>}
      {info.unassigned_active_staff > 0 && <p className="text-xs text-amber-600 mt-1" data-testid="ent-unassigned-staff">{info.unassigned_active_staff} active staff without a location assignment (grandfathered)</p>}
      <div className="flex gap-2 mt-3">
        <input className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ent-enforcement-reason" />
        <Button size="sm" variant="outline" onClick={() => toggle("register")} data-testid="ent-enforcement-toggle">{on ? "Disable" : "Enable"} register</Button>
        <Button size="sm" variant="outline" onClick={() => toggle("staff")} data-testid="ent-staff-enforcement-toggle">{sOn ? "Disable" : "Enable"} staff</Button>
      </div>
    </div>
  );
}


export default function EntitlementsDialog({ client, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null); // {kind, mode}
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/superadmin/clients/${client.id}/entitlements`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { if (client) load(); }, [client?.id]); // eslint-disable-line

  const submit = async () => {
    const clearing = edit.mode === "clear";
    const num = clearing ? null : parseInt(value, 10);
    if (!clearing && (!Number.isInteger(num) || num < 1)) { toast.error("Enter a whole number of 1 or more"); return; }
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    const label = data.items[edit.kind].label;
    if (!window.confirm(clearing ? `Clear the ${label} override for ${client.business_name}? The standard limit will apply again.` : `Set ${label} override to ${num} for ${client.business_name}?\n\nThis grants entitlement only. It does not change billing or Stripe.`)) return;
    setBusy(true);
    try {
      const { data: d } = await api.put(`/superadmin/clients/${client.id}/entitlements/override`, { kind: edit.kind, value: num, reason: reason.trim() });
      setData(d); setEdit(null); setValue(""); setReason("");
      toast.success(clearing ? "Override cleared" : "Override applied");
      onChanged?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="entitlements-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">POS Entitlements</DialogTitle>
          <DialogDescription>{client?.business_name}. Entitlement is separate from billing: overrides never create charges or change Stripe state.</DialogDescription>
        </DialogHeader>
        {!data ? <p className="text-sm text-muted-foreground">Loading...</p> : (
          <div className="space-y-3">
            {ORDER.map((k) => <Row key={k} kind={k} it={data.items[k]} onEdit={(kind, mode) => { setEdit({ kind, mode }); setValue(data.items[kind].override ?? ""); setReason(""); }} />)}
            <RegistersPanel client={client} />
            <p className="text-[11px] text-muted-foreground" data-testid="ent-notes">
              Staff counts active manager and cashier accounts (tenant level today). Registers are counted per location (busiest location shown above); each location gets its own allowance. Enforcement is a per-tenant flag, default OFF for existing customers.
            </p>
          </div>
        )}
        {edit && data && (
          <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-3" data-testid="ent-edit-form">
            <p className="font-head font-bold text-sm">{edit.mode === "clear" ? "Clear" : "Set"} {data.items[edit.kind].label} override</p>
            {edit.mode === "apply" && (
              <div><Label>New limit</Label><Input type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} data-testid="ent-value-input" /></div>
            )}
            <div><Label>Reason (required)</Label><Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Courtesy increase for launch customer" data-testid="ent-reason-input" /></div>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setEdit(null)} data-testid="ent-cancel-btn">Cancel</Button>
              <Button size="sm" disabled={busy} onClick={submit} data-testid="ent-submit-btn">{edit.mode === "clear" ? "Clear Override" : "Apply Override"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
